import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bell, Check, ExternalLink, GitMerge, Mail, Pencil, Phone, Smartphone, Trash2, User, X } from 'lucide-react'
import { announcementsApi, type MemberDetail, type MemberPatch, type MemberSummary } from '../../lib/infoserviceApi.js'
import {
  MEMBER_CATEGORIES,
  categoryLabel,
  feeLabel,
  frenchDate,
  membershipYears,
  quotaTone,
  renewalDay,
  weeksLabel,
} from '../../lib/memberQuota.js'
import { isDayKey, shortDayLabel } from '../../lib/announcementGrid.js'
import { Spinner } from '../common/Spinner.js'
import { QuotaGauge } from './QuotaGauge.js'

interface MemberDrawerProps {
  id: string
  members: MemberSummary[]
  canManage: boolean
  /** Année civile en cours, pour la frise des années d'adhésion. */
  currentYear: number
  onClose: () => void
  onChanged: () => void
  onDeleted: () => void
  onMerged: (intoId: string) => void
  onOpenAnnouncement: (id: string) => void
}

interface FormState {
  name: string
  membershipDate: string
  weekQuota: string
  category: string
  fee: string
  followedBy: string
  reminder: string
  newsletter: '' | 'yes' | 'no'
  memberYears: number[]
  contactName: string
  email: string
  phone: string
  mobile: string
  secondaryContact: string
  address: string
  postalCode: string
  city: string
  notes: string
}

function formOf(member: MemberDetail): FormState {
  return {
    name: member.name,
    membershipDate: member.membershipDate ?? '',
    weekQuota: String(member.weekQuota),
    category: member.category ? String(member.category) : '',
    fee: member.fee ?? '',
    followedBy: member.followedBy ?? '',
    reminder: member.reminder ?? '',
    newsletter: member.newsletter === null ? '' : member.newsletter ? 'yes' : 'no',
    memberYears: [...member.memberYears],
    contactName: member.contactName ?? '',
    email: member.email ?? '',
    phone: member.phone ?? '',
    mobile: member.mobile ?? '',
    secondaryContact: member.secondaryContact ?? '',
    address: member.address ?? '',
    postalCode: member.postalCode ?? '',
    city: member.city ?? '',
    notes: member.notes ?? '',
  }
}

const TEXT_FIELDS = ['fee', 'followedBy', 'reminder', 'contactName', 'email', 'phone', 'mobile', 'secondaryContact', 'address', 'postalCode', 'city', 'notes'] as const

function patchOf(form: FormState): MemberPatch {
  const patch: MemberPatch = {
    name: form.name.trim(),
    membershipDate: form.membershipDate || null,
    weekQuota: Number(form.weekQuota),
    category: form.category ? Number(form.category) : null,
    newsletter: form.newsletter === '' ? null : form.newsletter === 'yes',
    memberYears: [...form.memberYears].sort((a, b) => a - b),
  }
  for (const field of TEXT_FIELDS) patch[field] = form[field].trim() || null
  return patch
}

type SectionId = 'membership' | 'years' | 'contacts' | 'address'

/**
 * Fiche adhérent (maquette `designs/infoservice/adherents/fiche-adherent.pen`) :
 * une fiche qui se **consulte** ; « Modifier » (par partie ou en pied) passe
 * toute la fiche en saisie, Enregistrer en bas à gauche.
 */
export const MemberDrawer = memo(function MemberDrawer({
  id, members, canManage, currentYear, onClose, onChanged, onDeleted, onMerged, onOpenAnnouncement,
}: MemberDrawerProps) {
  const [member, setMember] = useState<MemberDetail | null>(null)
  const [form, setForm] = useState<FormState | null>(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [merging, setMerging] = useState(false)
  const [mergeInto, setMergeInto] = useState('')
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    announcementsApi.members.get(id)
      .then(detail => { if (!cancelled) { setMember(detail); setForm(formOf(detail)) } })
      .catch(cause => { if (!cancelled) setMessage({ tone: 'error', text: cause instanceof Error ? cause.message : 'Chargement impossible' }) })
    return () => { cancelled = true }
  }, [id])

  const dirty = member !== null && form !== null && JSON.stringify(patchOf(form)) !== JSON.stringify(patchOf(formOf(member)))
  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(current => (current ? { ...current, [key]: value } : current))
  }, [])

  /** « Modifier » d'une partie : toute la fiche passe en saisie, le premier champ de la partie reçoit le focus. */
  const edit = useCallback((section: SectionId) => {
    setEditing(true)
    setMerging(false)
    setMessage(null)
    requestAnimationFrame(() => {
      const target = bodyRef.current?.querySelector<HTMLElement>(`[data-section="${section}"] input, [data-section="${section}"] select, [data-section="${section}"] textarea, [data-section="${section}"] button[aria-pressed]`)
      target?.focus()
      target?.scrollIntoView({ block: 'nearest' })
    })
  }, [])

  const cancelEdit = useCallback(() => {
    if (member) setForm(formOf(member))
    setEditing(false)
    setMessage(null)
  }, [member])

  const close = useCallback(() => {
    if (dirty && !window.confirm('Quitter la fiche sans enregistrer les modifications ?')) return
    onClose()
  }, [dirty, onClose])

  const save = useCallback(async () => {
    if (!member || !form) return
    if (!form.name.trim()) {
      setMessage({ tone: 'error', text: 'Le nom est obligatoire.' })
      return
    }
    if (form.membershipDate && !isDayKey(form.membershipDate)) {
      setMessage({ tone: 'error', text: 'La date d’adhésion est incomplète.' })
      return
    }
    const quota = Number(form.weekQuota)
    if (!Number.isInteger(quota) || quota < 0 || quota > 53) {
      setMessage({ tone: 'error', text: 'Le droit annuel est un nombre de semaines, de 0 à 53.' })
      return
    }
    setBusy('save')
    setMessage(null)
    try {
      const updated = await announcementsApi.members.update(member.id, patchOf(form))
      setMember(updated)
      setForm(formOf(updated))
      setEditing(false)
      setMessage({ tone: 'ok', text: 'Fiche enregistrée' })
      onChanged()
    } catch (cause) {
      setMessage({ tone: 'error', text: cause instanceof Error ? cause.message : 'Enregistrement impossible' })
    } finally {
      setBusy(null)
    }
  }, [form, member, onChanged])

  const merge = useCallback(async () => {
    if (!member || !mergeInto) return
    const target = members.find(entry => entry.id === mergeInto)
    if (!window.confirm(`Fusionner « ${member.name} » dans « ${target?.name ?? ''} » ? Ses annonces et ses écritures y passent, et son nom y reste reconnu.`)) return
    setBusy('merge')
    try {
      await announcementsApi.members.merge(member.id, mergeInto)
      onMerged(mergeInto)
    } catch (cause) {
      setMessage({ tone: 'error', text: cause instanceof Error ? cause.message : 'Fusion impossible' })
      setBusy(null)
    }
  }, [member, members, mergeInto, onMerged])

  const remove = useCallback(async () => {
    if (!member) return
    const count = member.announcements.length
    const consequence = count ? ` Ses ${count} annonce${count > 1 ? 's' : ''} repasseront « à rattacher ».` : ''
    if (!window.confirm(`Supprimer l’adhérent « ${member.name} » ?${consequence}`)) return
    setBusy('delete')
    try {
      await announcementsApi.members.remove(member.id)
      onDeleted()
    } catch (cause) {
      setMessage({ tone: 'error', text: cause instanceof Error ? cause.message : 'Suppression impossible' })
      setBusy(null)
    }
  }, [member, onDeleted])

  const years = useMemo(() => membershipYears(editing && form ? form.memberYears : member?.memberYears ?? [], currentYear), [currentYear, editing, form, member])
  const readOnly = !canManage
  const inPeriod = member?.announcements.filter(entry => entry.days > 0) ?? []
  const outside = member?.announcements.filter(entry => entry.days === 0) ?? []
  const renewal = renewalDay(member?.period ?? null)

  return (
    <aside className="announcement-drawer member-drawer" aria-label={member ? `Fiche de ${member.name}` : 'Fiche adhérent'}>
      <header className="announcement-drawer__header">
        <div className="announcement-drawer__title">
          <span className="announcement-drawer__organization">{member?.name ?? '…'}</span>
          {member && (
            <>
              <span className="announcement-drawer__event">{memberSubtitle(member)}</span>
              <span className="member-drawer__chips">
                <span className="announcement-drawer__status is-info">{member.category ? `C${member.category} · ${MEMBER_CATEGORIES[member.category] ?? ''}` : 'Sans catégorie'}</span>
                {feeLabel(member.fee) && <span className="announcement-drawer__status is-muted">{feeLabel(member.fee)}</span>}
                {member.period
                  ? <span className={`announcement-drawer__status ${member.expired ? 'is-danger' : 'is-muted'}`}>{member.expired ? `Échue le ${frenchDate(renewal)}` : `Adhésion jusqu’au ${frenchDate(member.period.end)}`}</span>
                  : <span className="announcement-drawer__status is-warning">Date d’adhésion à renseigner</span>}
                {member.reminder && <span className="announcement-drawer__status is-warning">Relance : {member.reminder}</span>}
              </span>
            </>
          )}
        </div>
        <button type="button" className="tp-icon-button" onClick={close} aria-label="Fermer la fiche"><X size={16} aria-hidden="true" /></button>
      </header>

      <div className="announcement-drawer__body member-drawer__body" ref={bodyRef}>
        {message && (
          <div className={`tp-feedback ${message.tone === 'ok' ? 'tp-feedback--success' : 'tp-feedback--error'}`} role={message.tone === 'ok' ? 'status' : 'alert'}>{message.text}</div>
        )}
        {!member || !form ? <div className="tp-empty-state"><Spinner /> Chargement…</div> : (
          <>
            <section className="member-drawer__quota" aria-label="Semaines d’annonces">
              <span className="member-drawer__label">Semaines d’annonces consommées</span>
              <div className="member-drawer__quota-line">
                <span className="member-quota__value">{member.weeksUsed} <span className="member-quota__of">/ {member.quota} par an</span></span>
                <span className="member-quota__detail">
                  {member.weeksPassed} passée{member.weeksPassed > 1 ? 's' : ''} · {member.weeksPlanned} prévue{member.weeksPlanned > 1 ? 's' : ''}
                  {member.weeksOver > 0
                    ? <> · <strong className="member-quota__over">{member.weeksOver} de trop</strong></>
                    : <> · {member.quota - member.weeksUsed} restante{member.quota - member.weeksUsed > 1 ? 's' : ''}</>}
                </span>
              </div>
              <QuotaGauge member={member} wide />
              <p className="announcement-drawer__hint">
                {member.period
                  ? `Année d’adhésion du ${frenchDate(member.period.start)} au ${frenchDate(member.period.end)}. Une semaine est décomptée dès qu’une annonce de l’adhérent y est cochée.`
                  : 'Sans date d’adhésion, toutes ses semaines d’annonces sont comptées.'}
                {quotaTone(member) === 'over' && ' Le dépassement est signalé, jamais bloqué.'}
              </p>
            </section>

            <Section title="Adhésion" section="membership" editing={editing} readOnly={readOnly} onEdit={edit}>
              {editing ? (
                <fieldset className="announcement-drawer__fields" disabled={busy !== null}>
                  <label className="tp-form-field announcement-drawer__wide">
                    <span className="tp-field-label">Nom</span>
                    <input className="tp-input" value={form.name} onChange={event => set('name', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Catégorie</span>
                    <select className="tp-input" value={form.category} onChange={event => set('category', event.target.value)}>
                      <option value="">Sans catégorie</option>
                      {Object.entries(MEMBER_CATEGORIES).map(([value, label]) => <option key={value} value={value}>C{value} · {label}</option>)}
                    </select>
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Montant HT</span>
                    <input className="tp-input" value={form.fee} placeholder="65,00" onChange={event => set('fee', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Adhésion ou dernier renouvellement</span>
                    <input className="tp-input" type="date" value={form.membershipDate} onChange={event => set('membershipDate', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Droit annuel (semaines)</span>
                    <input className="tp-input" type="number" min={0} max={53} inputMode="numeric" value={form.weekQuota} onChange={event => set('weekQuota', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Relance</span>
                    <input className="tp-input" value={form.reminder} placeholder="14/10/2026" onChange={event => set('reminder', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Chargé de suivi</span>
                    <input className="tp-input" value={form.followedBy} placeholder="LJ" onChange={event => set('followedBy', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Newsletter</span>
                    <select className="tp-input" value={form.newsletter} onChange={event => set('newsletter', event.target.value as FormState['newsletter'])}>
                      <option value="">Non renseigné</option>
                      <option value="yes">Oui</option>
                      <option value="no">Non</option>
                    </select>
                  </label>
                  <label className="tp-form-field announcement-drawer__wide">
                    <span className="tp-field-label">Notes</span>
                    <textarea className="tp-textarea" rows={2} value={form.notes} onChange={event => set('notes', event.target.value)} />
                  </label>
                </fieldset>
              ) : (
                <dl className="member-drawer__facts">
                  <Fact label="Catégorie" value={member.category ? categoryLabel(member.category).replace(' · ', ' — ') : null} />
                  <Fact label="Montant" value={feeLabel(member.fee)} />
                  <Fact label="Adhésion ou dernier renouvellement" value={member.membershipDate ? frenchDate(member.membershipDate) : null} data />
                  <Fact label="Échéance" value={renewal ? frenchDate(renewal) : null} data />
                  <Fact label="Relance" value={member.reminder} icon={<Bell size={14} aria-hidden="true" />} />
                  <Fact label="Chargé de suivi" value={member.followedBy} icon={<User size={14} aria-hidden="true" />} />
                  <Fact label="Droit annuel" value={`${weeksLabel(member.weekQuota)} d’annonces`} />
                  <Fact label="Newsletter" value={member.newsletter === null ? null : member.newsletter ? 'Oui' : 'Non'} icon={member.newsletter ? <Check size={14} aria-hidden="true" /> : undefined} />
                  {member.notes && <Fact label="Notes" value={member.notes} wide />}
                </dl>
              )}
            </Section>

            <Section title="Années d’adhésion" section="years" editing={editing} readOnly={readOnly} onEdit={edit}>
              <p className="announcement-drawer__hint">{years.summary}</p>
              {years.years.length > 0 && (
                <ul className="member-drawer__years" aria-label="Années d’adhésion">
                  {years.years.map(entry => (
                    <li key={entry.year}>
                      {editing ? (
                        <button type="button" className={`member-drawer__year${entry.member ? ' is-member' : ''}`} aria-pressed={entry.member}
                          onClick={() => set('memberYears', entry.member ? form.memberYears.filter(year => year !== entry.year) : [...form.memberYears, entry.year])}>
                          {entry.year}
                        </button>
                      ) : (
                        <span className={`member-drawer__year${entry.member ? ' is-member' : ''}`} title={entry.member ? 'Adhérent cette année-là' : 'Pas adhérent cette année-là'}>{entry.year}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {editing && (
                <button type="button" className="tp-button member-drawer__add-year"
                  onClick={() => set('memberYears', [...form.memberYears, form.memberYears.length ? Math.min(...form.memberYears) - 1 : currentYear])}>
                  + {form.memberYears.length ? `Ajouter ${Math.min(...form.memberYears) - 1}` : `Ajouter ${currentYear}`}
                </button>
              )}
            </Section>

            <Section title="Contacts" section="contacts" editing={editing} readOnly={readOnly} onEdit={edit}>
              {editing ? (
                <fieldset className="announcement-drawer__fields" disabled={busy !== null}>
                  <label className="tp-form-field announcement-drawer__wide">
                    <span className="tp-field-label">Interlocuteur principal</span>
                    <input className="tp-input" value={form.contactName} onChange={event => set('contactName', event.target.value)} />
                  </label>
                  <label className="tp-form-field announcement-drawer__wide">
                    <span className="tp-field-label">Mail</span>
                    <input className="tp-input" type="text" inputMode="email" value={form.email} onChange={event => set('email', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Téléphone</span>
                    <input className="tp-input" type="tel" value={form.phone} onChange={event => set('phone', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Portable</span>
                    <input className="tp-input" type="tel" value={form.mobile} onChange={event => set('mobile', event.target.value)} />
                  </label>
                  <label className="tp-form-field announcement-drawer__wide">
                    <span className="tp-field-label">Second contact</span>
                    <textarea className="tp-textarea" rows={2} value={form.secondaryContact} onChange={event => set('secondaryContact', event.target.value)} />
                  </label>
                </fieldset>
              ) : (
                <div className="member-drawer__contacts">
                  <div className="member-drawer__card">
                    <span className="member-drawer__card-label">Interlocuteur principal</span>
                    {member.contactName || member.email || member.phone || member.mobile ? (
                      <>
                        {member.contactName && <span className="member-drawer__card-name">{member.contactName}</span>}
                        {member.email && <ContactLine icon={<Mail size={14} aria-hidden="true" />} label="Mail">{splitEmails(member.email).map(email => <a key={email} href={`mailto:${email}`}>{email}</a>)}</ContactLine>}
                        {member.phone && <ContactLine icon={<Phone size={14} aria-hidden="true" />} label="Téléphone"><a className="member-drawer__data" href={`tel:${member.phone.replace(/[^\d+]/g, '')}`}>{member.phone}</a></ContactLine>}
                        {member.mobile && <ContactLine icon={<Smartphone size={14} aria-hidden="true" />} label="Portable"><a className="member-drawer__data" href={`tel:${member.mobile.replace(/[^\d+]/g, '')}`}>{member.mobile}</a></ContactLine>}
                      </>
                    ) : <span className="members-table__missing">Non renseigné</span>}
                  </div>
                  <div className="member-drawer__card">
                    <span className="member-drawer__card-label">Second contact</span>
                    {member.secondaryContact
                      ? <span className="member-drawer__card-text">{member.secondaryContact}</span>
                      : <span className="members-table__missing">Non renseigné</span>}
                  </div>
                </div>
              )}
            </Section>

            <Section title="Adresse" section="address" editing={editing} readOnly={readOnly} onEdit={edit}>
              {editing ? (
                <fieldset className="announcement-drawer__fields" disabled={busy !== null}>
                  <label className="tp-form-field announcement-drawer__wide">
                    <span className="tp-field-label">Adresse</span>
                    <textarea className="tp-textarea" rows={2} value={form.address} onChange={event => set('address', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Code postal</span>
                    <input className="tp-input" inputMode="numeric" value={form.postalCode} onChange={event => set('postalCode', event.target.value)} />
                  </label>
                  <label className="tp-form-field">
                    <span className="tp-field-label">Ville</span>
                    <input className="tp-input" value={form.city} onChange={event => set('city', event.target.value)} />
                  </label>
                </fieldset>
              ) : (
                <dl className="member-drawer__facts">
                  <Fact label="Adresse" value={member.address} wide />
                  <Fact label="Code postal" value={member.postalCode} data />
                  <Fact label="Ville" value={member.city} />
                </dl>
              )}
            </Section>

            <section className="announcement-drawer__section">
              <h3 className="announcement-drawer__heading">
                Annonces de l’année d’adhésion <span className="announcement-drawer__count">{inPeriod.length}</span>
              </h3>
              <p className="announcement-drawer__hint">
                {member.spellings.length
                  ? <>Écrit dans la feuille : {member.spellings.map((spelling, index) => <span key={spelling}>{index > 0 && ' · '}<span className="member-drawer__spelling">{spelling}</span></span>)}</>
                  : 'Aucune annonce de la feuille ne lui est encore rattachée.'}
              </p>
              {inPeriod.length > 0 && <MemberAnnouncementList entries={inPeriod} onOpen={onOpenAnnouncement} />}
              {outside.length > 0 && (
                <details className="member-announcements__outside">
                  <summary>Hors de l’année d’adhésion ({outside.length})</summary>
                  <MemberAnnouncementList entries={outside} onOpen={onOpenAnnouncement} />
                </details>
              )}
            </section>

            {editing && (
              <section className="announcement-drawer__section" aria-label="Supprimer l’adhérent">
                <div className="announcement-drawer__manage">
                  <button type="button" className="tp-button tp-button--danger-outline" disabled={busy !== null} onClick={() => void remove()}>
                    {busy === 'delete' ? <Spinner /> : <Trash2 size={14} aria-hidden="true" />} Supprimer l’adhérent
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {!readOnly && member && (
        <footer className={`announcement-drawer__footer member-drawer__footer${editing || merging ? '' : ' is-consulting'}`}>
          {editing ? (
            <>
              <button type="button" className="tp-button tp-button--primary" disabled={!dirty || busy !== null} onClick={() => void save()}>
                {busy === 'save' && <Spinner />} Enregistrer
              </button>
              <button type="button" className="tp-button" disabled={busy !== null} onClick={cancelEdit}>Annuler</button>
            </>
          ) : merging ? (
            <>
              <select className="tp-input member-merge__select" aria-label="Fusionner dans" value={mergeInto} onChange={event => setMergeInto(event.target.value)} autoFocus>
                <option value="">Choisir l’adhérent à garder…</option>
                {members.filter(entry => entry.id !== member.id).map(entry => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
              </select>
              <button type="button" className="tp-button tp-button--primary" disabled={!mergeInto || busy !== null} onClick={() => void merge()}>
                {busy === 'merge' ? <Spinner /> : <GitMerge size={14} aria-hidden="true" />} Fusionner
              </button>
              <button type="button" className="tp-button" disabled={busy !== null} onClick={() => { setMerging(false); setMergeInto('') }}>Annuler</button>
            </>
          ) : (
            <>
              <button type="button" className="tp-button" onClick={() => setMerging(true)}>
                <GitMerge size={14} aria-hidden="true" /> Fusionner avec un autre adhérent
              </button>
              <button type="button" className="tp-button tp-button--primary member-drawer__edit" onClick={() => edit('membership')}>
                <Pencil size={14} aria-hidden="true" /> Modifier la fiche
              </button>
            </>
          )}
        </footer>
      )}
    </aside>
  )
})

/** « Association · adhérente depuis 2002 », d'après la catégorie et les années notées. */
function memberSubtitle(member: MemberDetail): string {
  const kind = member.category === 4 ? 'Particulier'
    : member.category === 5 || member.category === 9 ? 'Entreprise'
      : member.category === 3 || member.category === 6 || member.category === 7 ? 'Collectivité'
        : member.category ? 'Association' : 'Adhérent'
  if (member.memberYears.length === 0) return kind
  const first = Math.min(...member.memberYears)
  const contiguous = member.memberYears.length === Math.max(...member.memberYears) - first + 1
  return `${kind} · adhérent ${contiguous ? 'sans interruption ' : ''}depuis ${first}`
}

function splitEmails(value: string): string[] {
  const emails = value.match(/[^\s<>,;/]+@[^\s<>,;/]+/g)
  return emails?.length ? emails : [value]
}

const Section = memo(function Section({ title, section, editing, readOnly, onEdit, children }: {
  title: string
  section: SectionId
  editing: boolean
  readOnly: boolean
  onEdit: (section: SectionId) => void
  children: React.ReactNode
}) {
  return (
    <section className="announcement-drawer__section member-drawer__section" data-section={section}>
      <div className="member-drawer__section-head">
        <h3 className="announcement-drawer__heading">{title}</h3>
        {!readOnly && !editing && (
          <button type="button" className="member-drawer__link" onClick={() => onEdit(section)} aria-label={`Modifier : ${title}`}>Modifier</button>
        )}
      </div>
      {children}
    </section>
  )
})

const Fact = memo(function Fact({ label, value, data = false, wide = false, icon }: { label: string; value: string | null; data?: boolean; wide?: boolean; icon?: React.ReactNode }) {
  return (
    <div className={`member-drawer__fact${wide ? ' announcement-drawer__wide' : ''}`}>
      <dt className="member-drawer__label">{label}</dt>
      <dd className={`member-drawer__value${data ? ' member-drawer__data' : ''}`}>
        {value ? <>{icon}{value}</> : <span className="members-table__missing">Non renseigné</span>}
      </dd>
    </div>
  )
})

const ContactLine = memo(function ContactLine({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <span className="member-drawer__contact-line">
      <span className="member-drawer__contact-icon" title={label}>{icon}</span>
      <span className="member-drawer__contact-value">{children}</span>
    </span>
  )
})

export const MemberAnnouncementList = memo(function MemberAnnouncementList({ entries, onOpen }: { entries: MemberDetail['announcements']; onOpen: (id: string) => void }) {
  return (
    <ul className="member-announcements">
      {entries.map(entry => (
        <li key={entry.id} className={`member-announcements__item${entry.cancelled ? ' is-cancelled' : ''}`}>
          <span className="member-announcements__main">
            <button type="button" className="member-announcements__open" onClick={() => onOpen(entry.id)} title="Ouvrir la fiche de l’annonce">
              <span className="member-announcements__title">{entry.eventLabel || entry.organizationLabel}</span>
              <ExternalLink size={12} aria-hidden="true" />
            </button>
            <span className="member-announcements__meta">
              {entry.firstDay && entry.lastDay
                ? `${shortDayLabel(entry.firstDay)} → ${shortDayLabel(entry.lastDay)}`
                : `${entry.outsideDays} jour${entry.outsideDays > 1 ? 's' : ''} hors période`}
              {entry.cancelled && ' · annulée'}
              {entry.archived && ' · archivée'}
            </span>
          </span>
          <span className={`announcement-drawer__status ${entry.weeks > 0 && !entry.cancelled ? 'is-success' : 'is-muted'}`}>
            {entry.cancelled ? 'Non décomptée' : entry.weeks > 0 ? weeksLabel(entry.weeks) : 'Aucune semaine cochée'}
          </span>
        </li>
      ))}
    </ul>
  )
})
